import { StatusIndicatorProps } from "@amzn/awsui-components-react/polaris/status-indicator/internal";
import { AccountingEvent } from "../types/events";
import { PAYMENT_METHODS } from "../constants";
import { mapEmpProviderToPaymentMethod } from "./PaymentUtils";

export const isTvodAccountingEvent = (event: AccountingEvent) => {
  return Number(event.glProductGroup) === 318;
};

const createState = (status: StatusIndicatorProps.Type, text: string) => ({
  status,
  text,
});

export const getPaymentMethod = (event: AccountingEvent): string => {
  if (event.isEmpOrder) {
    if (event.empProviderId) {
      return mapEmpProviderToPaymentMethod(event.empProviderId);
    }
    return PAYMENT_METHODS.EMP;
  }

  const paymentId = Object.keys(event.paymentInstruments || {})[0];
  const payment = event.paymentInstruments?.[paymentId];
  switch (payment?.instrument) {
    case "BankAccountPaymentInstrument":
      return PAYMENT_METHODS.DIRECT_DEBIT;
    case "CreditCardPaymentInstrument":
      return PAYMENT_METHODS.CREDIT_CARD;
    default:
      return payment?.instrument ?? PAYMENT_METHODS.AMP;
  }
};

export const getPaymentState = (event: AccountingEvent) => {
  const method = getPaymentMethod(event);
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (!method) return createState("error", "No Payment Data");
  if (event.isFreeTrial)
    return createState("in-progress", `Free Trial (${method})`);
  if (events.includes("OrderSettleSucceeded"))
    return createState("success", `Paid (${method})`);
  if (events.includes("OrderCompleted-ClosedNonPayment"))
    return createState("error", `Failed (${method})`);
  return createState("in-progress", `Pending (${method})`);
};

export const getFulfillmentState = (event: AccountingEvent) => {
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (
    !isTvodAccountingEvent(event) &&
    !(events.includes("PeriodStart") || events.includes("Resubscribed"))
  ) {
    return createState("error", "No Period Start");
  }
  if (events.includes("Cancelled")) return createState("error", "Cancelled");
  if (
    events.includes("PeriodStart") ||
    events.includes("Resubscribed") ||
    (isTvodAccountingEvent(event) && events.includes("OrderFulfilled"))
  ) {
    return createState("success", "Fulfilled");
  }
  return createState("in-progress", "Pending");
};

export const getRefundState = (event: AccountingEvent) => {
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (events.includes("RefundSucceeded"))
    return createState("success", "Refunded");
  if (events.includes("RefundFailed"))
    return createState("error", "Refund Failed");
  return createState("in-progress", "No Refund");
};

export const hasPromotions = (event: AccountingEvent) =>
  event.discountsOnRevenue && Object.keys(event.discountsOnRevenue).length > 0;

export const getEMPOrchestrationPlatform = (event: AccountingEvent) => 
  event.empPurchaseHandleId?.trim() ? "Thunderbird" : "Denarius";
